import React, { useId } from 'react';
import { View, Text, StyleSheet, Image } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import type { Propuesta } from '../lib/services/propuestas';
import { getImageDisplayUrl } from '../lib/utils/imageTransform';

type Props = {
  propuesta: Propuesta;
  /** Si es false, no se muestra el bloque blanco de Instagram bajo el hero (como en `/votar` por defecto true). */
  showInstagram?: boolean;
};

/**
 * Foto portada con degradado inferior y (opcional) bloque Instagram; sin título/logo superpuestos sobre la foto.
 */
export function PropuestaPortadaVotacion({ propuesta, showInstagram = true }: Props) {
  const heroOverlayGradId = `propuestaPortadaGrad_${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const miniFichaMetaRow = () => (
    <View style={styles.propuestaMetaHeaderRow}>
      <View style={styles.propuestaMetaLogoContainer}>
        {propuesta.restaurante?.imagen_principal?.trim() ? (
          <Image
            source={{
              uri:
                getImageDisplayUrl(propuesta.restaurante.imagen_principal, { preset: 'logo' }) ??
                propuesta.restaurante.imagen_principal.replace(/^http:/, 'https:'),
            }}
            style={styles.propuestaMetaLogo}
            resizeMode="cover"
          />
        ) : (
          <View style={styles.propuestaMetaLogoPlaceholder}>
            <MaterialCommunityIcons name="silverware-fork-knife" size={28} color="#9ca3af" />
          </View>
        )}
      </View>
      <View style={styles.propuestaMetaTextCol}>
        <Text style={styles.propuestaMetaTitulo} numberOfLines={3} translate="no">
          {propuesta.titulo}
        </Text>
        {propuesta.restaurante ? (
          <Text style={styles.propuestaMetaRestaurante} numberOfLines={2} translate="no">
            {propuesta.restaurante.nombre_mostrar || propuesta.restaurante.nombre_oficial}
          </Text>
        ) : null}
      </View>
    </View>
  );

  return (
    <View style={styles.root}>
      {propuesta.foto_restaurante ? (
        <View style={styles.imageContainer}>
          <Image
            source={{
              uri:
                getImageDisplayUrl(propuesta.foto_restaurante, { preset: 'cover' }) ??
                propuesta.foto_restaurante,
            }}
            style={styles.propuestaImage}
            resizeMode="cover"
          />
          <View style={styles.propuestaMetaGradientBand} pointerEvents="none">
            <Svg width="100%" height="100%" preserveAspectRatio="none">
              <Defs>
                <LinearGradient
                  id={heroOverlayGradId}
                  x1="0"
                  y1="1"
                  x2="0"
                  y2="0"
                  gradientUnits="objectBoundingBox"
                >
                  <Stop offset="0" stopColor="#030213" stopOpacity={0.88} />
                  <Stop offset="0.42" stopColor="#030213" stopOpacity={0.22} />
                  <Stop offset="1" stopColor="#030213" stopOpacity={0} />
                </LinearGradient>
              </Defs>
              <Rect x="0" y="0" width="100%" height="100%" fill={`url(#${heroOverlayGradId})`} />
            </Svg>
          </View>
        </View>
      ) : (
        <View style={styles.propuestaMetaHeader}>{miniFichaMetaRow()}</View>
      )}

      {showInstagram && propuesta.instagram ? (
        <View
          style={[styles.infoSection, propuesta.foto_restaurante ? styles.infoSectionSolapaFoto : null]}
        >
          <View style={styles.instagramSection}>
            <Text style={styles.instagramLabel}>Instagram:</Text>
            <Text style={styles.instagramText} translate="no">
              @{propuesta.instagram}
            </Text>
          </View>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    width: '100%',
    alignSelf: 'stretch',
  },
  imageContainer: {
    width: '100%',
    height: 248,
    backgroundColor: '#f3f4f6',
    position: 'relative',
    overflow: 'hidden',
  },
  propuestaImage: {
    width: '100%',
    height: '100%',
  },
  propuestaMetaGradientBand: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 182,
  },
  propuestaMetaHeader: {
    paddingTop: 12,
    paddingHorizontal: 20,
    paddingBottom: 4,
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    overflow: 'hidden',
  },
  propuestaMetaHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  propuestaMetaLogoContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    overflow: 'hidden',
    backgroundColor: '#e5e7eb',
    borderWidth: 1,
    borderColor: '#CCCCCC',
    flexShrink: 0,
  },
  propuestaMetaLogo: {
    width: '100%',
    height: '100%',
  },
  propuestaMetaLogoPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: '#e5e7eb',
    alignItems: 'center',
    justifyContent: 'center',
  },
  propuestaMetaTextCol: {
    flex: 1,
    minWidth: 0,
  },
  propuestaMetaTitulo: {
    fontSize: 16,
    fontWeight: '700',
    color: '#030213',
    marginBottom: 4,
    lineHeight: 22,
  },
  propuestaMetaRestaurante: {
    fontSize: 14,
    fontWeight: '600',
    color: '#878787',
    lineHeight: 19,
  },
  infoSection: {
    padding: 24,
    paddingTop: 16,
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    overflow: 'hidden',
  },
  infoSectionSolapaFoto: {
    marginTop: -14,
    zIndex: 1,
  },
  instagramSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  instagramLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
  },
  instagramText: {
    fontSize: 14,
    color: '#3b82f6',
    fontWeight: '500',
  },
});
